'use client'

import { Button } from '@/components/ui/button'
import {
	Card,
	CardContent,
	CardDescription,
	CardFooter,
	CardHeader,
	CardTitle,
} from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { brandingConfig } from '@/config/branding'
import { ROUTES } from '@/config/routes'
import { zodResolver } from '@hookform/resolvers/zod'
import { Loader2 } from 'lucide-react'
import Link from 'next/link'
import { useForm } from 'react-hook-form'
import { useAuth } from '../hooks/use-auth'
import { registerSchema, type RegisterFormData } from '../schemas'

/**
 * Registration form component.
 * Uses React Hook Form + Zod for validation, Supabase for auth.
 */
export function RegisterForm() {
	const { register: registerUser, isLoading, error, clearError } = useAuth()

	const {
		register,
		handleSubmit,
		formState: { errors },
	} = useForm({
		resolver: zodResolver(registerSchema),
		defaultValues: {
			email: '',
			password: '',
			confirmPassword: '',
			firstName: '',
			lastName: '',
			acceptTerms: false,
		},
	})

	const onSubmit = async (data: RegisterFormData) => {
		clearError()
		await registerUser({
			email: data.email,
			password: data.password,
			confirmPassword: data.confirmPassword,
			firstName: data.firstName,
			lastName: data.lastName,
			acceptTerms: data.acceptTerms as boolean,
		})
	}

	return (
		<Card className="mx-auto w-full max-w-md">
			<CardHeader className="text-center">
				<CardTitle className="text-2xl font-bold">Crear Cuenta</CardTitle>
				<CardDescription>Únete a {brandingConfig.appName}</CardDescription>
			</CardHeader>

			<form onSubmit={handleSubmit(onSubmit)}>
				<CardContent className="space-y-4">
					{error && (
						<div className="text-destructive bg-destructive/10 rounded-md p-3 text-sm">{error}</div>
					)}

					<div className="grid grid-cols-2 gap-4">
						<div className="space-y-2">
							<Label htmlFor="firstName">Nombre</Label>
							<Input
								id="firstName"
								placeholder="Juan"
								autoComplete="given-name"
								disabled={isLoading}
								{...register('firstName')}
							/>
						</div>
						<div className="space-y-2">
							<Label htmlFor="lastName">Apellido</Label>
							<Input
								id="lastName"
								placeholder="Pérez"
								autoComplete="family-name"
								disabled={isLoading}
								{...register('lastName')}
							/>
						</div>
					</div>

					<div className="space-y-2">
						<Label htmlFor="email">Correo electrónico</Label>
						<Input
							id="email"
							type="email"
							placeholder="tu@correo.com"
							autoComplete="email"
							disabled={isLoading}
							{...register('email')}
						/>
						{errors.email && <p className="text-destructive text-sm">{errors.email.message}</p>}
					</div>

					<div className="space-y-2">
						<Label htmlFor="password">Contraseña</Label>
						<Input
							id="password"
							type="password"
							placeholder="••••••••"
							autoComplete="new-password"
							disabled={isLoading}
							{...register('password')}
						/>
						{errors.password && (
							<p className="text-destructive text-sm">{errors.password.message}</p>
						)}
					</div>

					<div className="space-y-2">
						<Label htmlFor="confirmPassword">Confirmar contraseña</Label>
						<Input
							id="confirmPassword"
							type="password"
							placeholder="••••••••"
							autoComplete="new-password"
							disabled={isLoading}
							{...register('confirmPassword')}
						/>
						{errors.confirmPassword && (
							<p className="text-destructive text-sm">{errors.confirmPassword.message}</p>
						)}
					</div>

					<div className="flex items-start space-x-2">
						<Checkbox id="acceptTerms" {...register('acceptTerms')} />
						<Label htmlFor="acceptTerms" className="text-sm leading-relaxed">
							Acepto los{' '}
							<Link href="/terms" className="text-primary hover:underline">
								términos y condiciones
							</Link>{' '}
							y la{' '}
							<Link href="/privacy" className="text-primary hover:underline">
								política de privacidad
							</Link>
						</Label>
					</div>
					{errors.acceptTerms && (
						<p className="text-destructive text-sm">{errors.acceptTerms.message}</p>
					)}
				</CardContent>

				<CardFooter className="flex flex-col gap-4">
					<Button type="submit" className="w-full" disabled={isLoading}>
						{isLoading ? (
							<>
								<Loader2 className="mr-2 h-4 w-4 animate-spin" />
								Creando cuenta...
							</>
						) : (
							'Crear Cuenta'
						)}
					</Button>

					<p className="text-muted-foreground text-center text-sm">
						¿Ya tienes cuenta?{' '}
						<Link href={ROUTES.LOGIN} className="text-primary font-medium hover:underline">
							Inicia sesión
						</Link>
					</p>
				</CardFooter>
			</form>
		</Card>
	)
}
